import { z } from "zod";
import { PaginationParams } from "./pagination.js";
export const AGREEMENT_STATUSES = [
    "draft",
    "in_review",
    "finalising",
    "awaiting_signature",
    "completed",
    "declined",
];
export const AGREEMENT_TOPOLOGIES = ["bilateral", "deed"];
export const AgreementStatusEnum = z.enum(AGREEMENT_STATUSES);
export const AgreementTopologyEnum = z.enum(AGREEMENT_TOPOLOGIES);
export const AgreementDossierSchema = z.object({
    id: z.string().uuid(),
    title: z.string().min(1).max(500),
    status: AgreementStatusEnum,
    topology: AgreementTopologyEnum,
    workspaceDocumentId: z.string().uuid().nullable(),
    vaultDocumentId: z.string().uuid().nullable(),
    signatureRequestId: z.string().uuid().nullable(),
    engagementId: z.string().uuid().nullable(),
    companyId: z.string().uuid().nullable(),
    createdBy: z.string().email().max(320),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    archivedAt: z.string().datetime().nullable(),
});
export const AgreementCreateSchema = z.object({
    title: z.string().trim().min(1).max(500),
    topology: AgreementTopologyEnum.default("bilateral"),
    workspaceDocumentId: z.string().uuid().nullable().optional(),
    engagementId: z.string().uuid().nullable().optional(),
    companyId: z.string().uuid().nullable().optional(),
});
export const AgreementUpdateSchema = z.object({
    title: z.string().trim().min(1).max(500).optional(),
    status: AgreementStatusEnum.optional(),
    topology: AgreementTopologyEnum.optional(),
    workspaceDocumentId: z.string().uuid().nullable().optional(),
    vaultDocumentId: z.string().uuid().nullable().optional(),
    signatureRequestId: z.string().uuid().nullable().optional(),
    engagementId: z.string().uuid().nullable().optional(),
    companyId: z.string().uuid().nullable().optional(),
    archived: z.boolean().optional(),
});
export const AgreementListQuerySchema = PaginationParams.extend({
    status: AgreementStatusEnum.optional(),
    topology: AgreementTopologyEnum.optional(),
    engagementId: z.string().uuid().optional(),
    companyId: z.string().uuid().optional(),
    includeArchived: z
        .union([z.literal("true"), z.literal("false")])
        .optional()
        .transform((v) => v === "true"),
});
export const AgreementListResponseSchema = z.object({
    agreements: z.array(AgreementDossierSchema),
    total: z.number().int().nonnegative(),
});
export const AgreementLinkWorkspaceSchema = z.object({
    workspaceDocumentId: z.string().uuid(),
});
/** Shipped Word shells for prepare-from-working / prepare-from-text. */
export const AGREEMENT_SHELL_KINDS_SHIPPED = [
    "nda_bilateral",
    "generic_bilateral",
    "nda_deed",
    "generic_deed",
];
export const AgreementShellKindShippedEnum = z.enum(AGREEMENT_SHELL_KINDS_SHIPPED);
/** Staff-assigned section for one source paragraph (block-select prepare). */
export const AGREEMENT_PARAGRAPH_SECTIONS = [
    "exclude",
    "recitals",
    "operative",
    "schedules",
];
export const AgreementParagraphSectionEnum = z.enum(AGREEMENT_PARAGRAPH_SECTIONS);
export const AgreementDocxParagraphSchema = z.object({
    index: z.number().int().nonnegative(),
    text: z.string(),
    listLabel: z.string().nullable(),
});
/** List source paragraphs + suggested sections (no mutate). */
export const AgreementPrepareParagraphsQuerySchema = z.object({
    workingDocumentId: z.string().uuid(),
});
export const AgreementPrepareParagraphsResultSchema = z.object({
    sourceDocumentId: z.string().uuid(),
    sourceTitle: z.string(),
    paragraphs: z.array(AgreementDocxParagraphSchema),
    suggestedSections: z.array(AgreementParagraphSectionEnum),
    listMarkersFound: z.boolean().optional().default(false),
    looksLikePreparedShell: z.boolean().optional().default(false),
    warning: z.string().nullable().optional(),
});
/**
 * Prepare signing shell from a working document.
 *
 * Preferred (staff UI): `mode: "selection"` + `paragraphSections` — classify
 * Word paragraphs; list labels materialised from numbering.xml (no AI rewrite).
 * Legacy / MCP: omit mode (or `mode: "strip"`) — AI/heuristic cover/sig strip.
 */
export const AgreementPrepareFromWorkingSchema = z
    .object({
    workingDocumentId: z.string().uuid(),
    shellKind: AgreementShellKindShippedEnum,
    title: z.string().trim().min(1).max(500).optional(),
    collaborationSiteId: z.string().uuid().optional(),
    folderPath: z.string().max(1000).optional(),
    note: z.string().max(500).optional(),
    dryRun: z.boolean().optional().default(false),
    vaultFolderId: z.string().uuid().optional(),
    mode: z.enum(["selection", "strip"]).optional(),
    /** Required when mode is selection — one entry per source paragraph. */
    paragraphSections: z.array(AgreementParagraphSectionEnum).max(5_000).optional(),
})
    .superRefine((val, ctx) => {
    const mode = val.mode ?? (val.paragraphSections?.length ? "selection" : "strip");
    if (mode === "selection" && !val.paragraphSections?.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "paragraphSections is required when mode is selection",
            path: ["paragraphSections"],
        });
    }
});
export const AgreementPrepareFromWorkingResultSchema = z.object({
    dryRun: z.boolean(),
    sourceDocumentId: z.string().uuid(),
    sourceTitle: z.string(),
    rawCharCount: z.number().int().nonnegative(),
    bodyText: z.string(),
    recitalsText: z.string().optional(),
    schedulesText: z.string().optional(),
    strippedNotes: z.array(z.string()),
    stripMethod: z.enum(["ai", "heuristic", "selection"]),
    shellKind: z.string().optional(),
    documentId: z.string().uuid().optional(),
    webUrl: z.string().nullish(),
    finalName: z.string().optional(),
    siteName: z.string().optional(),
    signerTopology: z.enum(["bilateral", "deed"]).optional(),
    recommendedTemplateTag: z.string().optional(),
    nextSteps: z.array(z.string()).optional(),
    vaultDocumentId: z.string().uuid().nullable().optional(),
    archivedSourceDocumentId: z.string().uuid().optional(),
});
//# sourceMappingURL=agreements.js.map